import { shiftDate } from '../game/daily';
import type { Battle } from './protocol';
import { COHORT_DAYS, cohortSeg, type CohortSource, type Device, FIRST_STEPS, type FirstStep, type FirstVisit, type InputKind, SITE, STAY_DAYS, STAY_WITHIN, LAST_TALLY_KEY, type LastTally, type StatKey, type StatsBody, TIME_MAX } from './stats';

/**
 * The game's side of the analytics (see ./stats.ts): the visit's tally, sent
 * every couple of minutes and when the game is put away.
 */

/** This browser's days: the first it played, and the last two distinct days. */
interface Days {
  first: string;
  day: string;
  prev: string;
}
const DAYS_KEY = 'gp.days';

/**
 * This browser as of `today`: new (its first day), and the day it last played
 * before today. A browser that played before the stats began isn't new, and
 * its previous day isn't known.
 */
export function dayInfo(today: string, playedBefore: boolean, store: Pick<Storage, 'getItem' | 'setItem'> | null): { isNew: boolean; prevDay: string } {
  let d: Days | null = null;
  try {
    const raw = store?.getItem(DAYS_KEY);
    const o = raw ? (JSON.parse(raw) as Partial<Days>) : null;
    if (o && typeof o.first === 'string' && typeof o.day === 'string' && typeof o.prev === 'string') d = o as Days;
  } catch {
    d = null;
  }
  if (!d) d = { first: playedBefore ? '' : today, day: today, prev: '' };
  else if (d.day !== today) d = { first: d.first, day: today, prev: d.day };
  try {
    store?.setItem(DAYS_KEY, JSON.stringify(d));
  } catch {
    // No storage: every visit looks new, which is the best anyone can say.
  }
  return { isNew: d.first === today, prevDay: d.prev };
}

/** This browser's first day was the day before `today` (call dayInfo first: it keeps the days). */
function dayTwo(today: string, store: Pick<Storage, 'getItem'> | null): boolean {
  try {
    const o = JSON.parse(store?.getItem(DAYS_KEY) ?? 'null') as Partial<Days> | null;
    return !!o && o.first === shiftDate(today, -1);
  } catch {
    return false;
  }
}

/** The browser's storage, or null (blocked: some browsers throw just for asking). */
function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** The friends' links this browser has opened, newest last, and the steps already counted for each. */
const LINKS_KEY = 'gp.links';
const LINKS_KEPT = 40;
/** The day a friend's link brought this browser on its first day ('' or missing: it came some other way). */
const RECRUIT_KEY = 'gp.recruit';
/** Where a new player came from, kept from their first visit (net/arrival.ts), and whether it's been sent back once. */
const SITE_KEY = 'gp.site';

interface SiteNote {
  date: string;
  site: string;
  back?: boolean;
}

function readSite(store: Pick<Storage, 'getItem'> | null): SiteNote | null {
  try {
    const n = JSON.parse(store?.getItem(SITE_KEY) ?? 'null') as SiteNote | null;
    return n && typeof n.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(n.date) && typeof n.site === 'string' && SITE.test(n.site) ? n : null;
  } catch {
    return null;
  }
}

/**
 * A step of a friend's link (opened, watched, raced…) for the first time on this browser: true once, then false,
 * so the stats count people, not visits (opening the same link again, or reloading it, adds nothing).
 */
export function firstForLink(id: string, step: string, store: Pick<Storage, 'getItem' | 'setItem'> | null = storage()): boolean {
  let seen: [string, string[]][] = [];
  try {
    const o = JSON.parse(store?.getItem(LINKS_KEY) ?? '[]') as unknown;
    if (Array.isArray(o)) seen = o.filter((e): e is [string, string[]] => Array.isArray(e) && typeof e[0] === 'string' && Array.isArray(e[1]));
  } catch {
    seen = [];
  }
  const at = seen.findIndex(([k]) => k === id);
  const steps = at >= 0 ? seen[at][1] : [];
  if (steps.includes(step)) return false;
  if (at >= 0) seen.splice(at, 1);
  seen.push([id, [...steps, step]]);
  try {
    store?.setItem(LINKS_KEY, JSON.stringify(seen.slice(-LINKS_KEPT)));
  } catch {
    // No storage: every visit counts again, the best anyone can do.
  }
  return true;
}

/** Keep how the last tally went, for the stats page. */
function noteTally(result: string): void {
  try {
    const t: LastTally = { at: Date.now(), result };
    localStorage.setItem(LAST_TALLY_KEY, JSON.stringify(t));
  } catch {
    // No storage: the stats page just can't say.
  }
}

export interface StatsSource {
  playerId: string;
  device: Device;
  input: InputKind;
  /** Today in Rome. */
  today: string;
  /** The player has a name for the board. */
  named?: boolean;
  /** The player may be in a battle today (has a link of their own, or raced a friend's): ask for the news. */
  news?: boolean;
  /** Playing inside Discord. */
  discord?: boolean;
}

/** The day this browser was last touched (a key, a tap, a controller): it counts as a player all that day. */
const TOUCHED_KEY = 'gp.touched';

/**
 * A new player's first day, as this device sums it up (net/stats.ts FirstVisit): races started, the furthest
 * tenth of the lap, where unfinished races ended (`ends`, and the race in progress: `cur`, null once it has
 * finished a lap). Kept after that day until it has been sent back once (`back`), the first time the device returns.
 */
const FIRST_VISIT_KEY = 'gp.firstVisit';

export interface FirstVisitNote {
  date: string;
  device: Device;
  tries: number;
  far: number;
  ends: number[];
  cur: number | null;
  back?: boolean;
  /**
   * Cohorts (notes from 13 Oct 2026 on: v 2): how this player arrived on their first day, whether they took the
   * lesson and put a lap on the leaderboard that day; the days played so far, and the last day it said so (`ping`).
   */
  v?: 2;
  src?: CohortSource;
  lesson?: boolean;
  saved?: boolean;
  days?: number;
  ping?: string;
}

/** Whole days from one date to another ('2026-10-13' to '2026-10-16': 3). */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

function readFirstVisit(store: Pick<Storage, 'getItem'> | null): FirstVisitNote | null {
  try {
    const n = JSON.parse(store?.getItem(FIRST_VISIT_KEY) ?? 'null') as FirstVisitNote | null;
    const int = (v: unknown, lo: number, hi: number): boolean => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
    if (!n || typeof n.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(n.date) || (n.device !== 'phone' && n.device !== 'computer')) return null;
    if (!int(n.tries, 0, 1e6) || !int(n.far, 0, 10) || !Array.isArray(n.ends) || n.ends.length !== 10 || !n.ends.every((e) => int(e, 0, 1e6))) return null;
    if (n.cur !== null && !int(n.cur, 0, 9)) return null;
    if (n.v === 2 && ((n.src !== 'w' && n.src !== 'l' && n.src !== 'd') || !int(n.days, 1, 1e6) || typeof n.ping !== 'string')) return { ...n, v: undefined };
    return n;
  } catch {
    return null;
  }
}

/**
 * A first day's summary as sent: where most unfinished races ended, or -1. The race in progress counts for less
 * than one that has ended (it may get further yet): it only settles a tie, or stands alone.
 */
export function firstVisitOf(n: FirstVisitNote): FirstVisit | null {
  if (n.tries < 1) return null;
  const counts = n.ends.map((e, i) => 2 * e + (n.cur === i ? 1 : 0));
  let stuck = -1;
  counts.forEach((c, i) => {
    if (c > 0 && (stuck < 0 || c > counts[stuck])) stuck = i;
  });
  return { tries: Math.min(n.tries, 9999), far: n.far, stuck };
}
/** What counts as touching the game. */
const TOUCHES = ['keydown', 'pointerdown', 'touchstart', 'gamepadconnected'] as const;

/**
 * The tally this visit keeps, and sends. A browser new to the game counts as a player only once it's touched:
 * a page opened and left untouched is counted as just that ('opened'), since most such visits are automated
 * scanners, and they look like any browser. A returning browser counts straight away (someone checking the
 * board touches nothing).
 */
export class StatsReporter {
  private counts: Partial<Record<StatKey, number>> = {};
  private racingMs = 0;
  private watchingMs = 0;
  private saved = false;
  private visit = true;
  private started = false;
  /** Counted as a player: returning, or touched. */
  private engaged = false;
  /** Sent as opened and left (and, once touched after all, to be taken back). */
  private openedSent = false;
  private reopen = false;
  /** A friend's link brought this visit; and a link opened for the first time here, still to be sent (its id). */
  private viaLink = false;
  private linkOpen: string | null = null;
  /** The furthest of FIRST_STEPS this visit (-1: none yet), and the furthest already sent. */
  private reached = -1;
  private reachedSent = -1;
  /** The lesson taken, or its offer skipped (undefined: neither yet). */
  private tookLesson: boolean | undefined;
  /** The race in progress has finished a lap (its place round the lap no longer says where they got stuck). */
  private lappedThisRace = false;

  /** A battle's news, brought back by a tally (worker/index.ts): rivals whose laps are ahead of yours. */
  onNews: (battles: Battle[]) => void = () => {};

  constructor(
    private source: () => StatsSource,
    /** This browser played before the stats began (it already had a leaderboard id). */
    private playedBefore: boolean,
    private base = 'api/',
  ) {}

  /**
   * The visit begins (the game is on screen): say so now, then every couple of
   * minutes and on the way out. `skip`: why this visit isn't counted (a robot).
   */
  begin(skip: string | null = null): void {
    if (this.started) return;
    if (skip) {
      noteTally(`not counted: ${skip}`);
      return;
    }
    this.started = true;
    this.engaged = this.returning();
    if (this.engaged) this.send();
    else this.waitForTouch();
    window.setInterval(() => this.send(), 120_000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.leaving();
    });
    window.addEventListener('pagehide', () => this.leaving());
  }

  /** Played on another day, or touched earlier today. */
  private returning(): boolean {
    try {
      const s = this.source();
      if (!dayInfo(s.today, this.playedBefore, globalThis.localStorage ?? null).isNew) return true;
      return globalThis.localStorage?.getItem(TOUCHED_KEY) === s.today;
    } catch {
      return false;
    }
  }

  private waitForTouch(): void {
    const opts = { capture: true, passive: true };
    const touched = (): void => {
      for (const t of TOUCHES) window.removeEventListener(t, touched, opts);
      this.touched();
    };
    for (const t of TOUCHES) window.addEventListener(t, touched, opts);
  }

  /** First touch: a player from now on (all day), and the visit is sent. */
  touched(): void {
    if (this.engaged) return;
    this.engaged = true;
    this.reach('touched');
    try {
      globalThis.localStorage?.setItem(TOUCHED_KEY, this.source().today);
    } catch {
      // No storage: touched again next visit, that's all.
    }
    if (this.openedSent) this.reopen = true;
    this.send();
  }

  /** The page is going (or going out of sight): what's tallied goes, or just "opened and left". */
  private leaving(): void {
    if (this.engaged) this.send();
    else this.sendOpened();
  }

  private sendOpened(): void {
    if (!this.started || this.openedSent) return;
    let body: StatsBody;
    try {
      const s = this.source();
      const day = dayInfo(s.today, this.playedBefore, globalThis.localStorage ?? null);
      body = { playerId: s.playerId, device: s.device, input: s.input, visit: false, ...day, racingMs: 0, watchingMs: 0, saved: false, counts: {}, opened: true };
    } catch {
      return;
    }
    this.openedSent = true;
    this.post(body, 'opened and left untouched: not counted as a player');
  }

  count(k: StatKey, n = 1): void {
    this.counts[k] = (this.counts[k] ?? 0) + n;
    if (k === 'laps') {
      this.reach('lap');
      this.lappedThisRace = true;
      this.firstDay((d) => {
        d.far = 10;
        d.cur = null;
      });
    }
    // A race starts (restarts too): the one before ends where it got to, unless it finished a lap.
    if (k === 'raceStarts') {
      this.lappedThisRace = false;
      this.firstDay((d) => {
        if (d.cur !== null) d.ends[d.cur]++;
        d.cur = 0;
        d.tries += n;
      });
    }
  }

  /** Where the car is round the lap, in tenths (0–9): on a first day, the furthest this race and all day. */
  progress(tenth: number): void {
    if (this.lappedThisRace || !Number.isInteger(tenth) || tenth < 0 || tenth > 9) return;
    this.firstDay((d) => {
      if (d.cur === null || tenth <= d.cur) return false;
      d.cur = tenth;
      if (tenth > d.far) d.far = tenth;
    });
  }

  /** A change to the first day's note (only on the player's first day; `false`: nothing changed). */
  private firstDay(change: (d: FirstVisitNote) => void | false): void {
    try {
      const s = this.source();
      const store = globalThis.localStorage ?? null;
      if (!store || !dayInfo(s.today, this.playedBefore, store).isNew) return;
      let d = readFirstVisit(store);
      if (!d || d.date !== s.today) {
        // How they arrived: inside Discord, or a friend's link that first day (cameByLink notes it), or neither.
        const src: CohortSource = s.discord ? 'd' : store.getItem(RECRUIT_KEY) === s.today ? 'l' : 'w';
        d = { date: s.today, device: s.device, tries: 0, far: 0, ends: Array<number>(10).fill(0), cur: null, v: 2, src, lesson: false, saved: false, days: 1, ping: s.today };
      }
      if (change(d) === false) return;
      store.setItem(FIRST_VISIT_KEY, JSON.stringify(d));
    } catch {
      // No storage: this first day goes unsummed.
    }
  }

  racing(ms: number): void {
    this.racingMs += ms;
    if (ms > 0) this.reach('moving');
  }

  /** A first step reached (the furthest is what's kept): where a new player who never drives stopped. */
  reach(step: FirstStep): void {
    this.reached = Math.max(this.reached, FIRST_STEPS.indexOf(step));
  }

  /** The lesson's offer answered (taken wins: skipped, then taken from the poster, is taken). */
  lessonChoice(took: boolean): void {
    if (this.tookLesson !== true) this.tookLesson = took;
    if (took)
      this.firstDay((d) => {
        if (d.lesson) return false;
        d.lesson = true;
      });
  }

  watching(ms: number): void {
    this.watchingMs += ms;
  }

  markSaved(): void {
    this.saved = true;
    this.firstDay((d) => {
      if (d.saved) return false;
      d.saved = true;
    });
  }

  /**
   * A friend's link brought this visit (working, over or gone; never the player's own). `firstOpen`: its id, the
   * first time this browser opens it (one more person it reached). On the player's first day, they're a recruit.
   */
  cameByLink(firstOpen: string | null): void {
    this.viaLink = true;
    if (firstOpen) this.linkOpen = firstOpen;
    try {
      const store = globalThis.localStorage ?? null;
      const today = this.source().today;
      if (dayInfo(today, this.playedBefore, store).isNew && !store?.getItem(RECRUIT_KEY)) store?.setItem(RECRUIT_KEY, today);
    } catch {
      // No storage: not a recruit, as far as anyone can tell.
    }
    // A first day a friend's link brought (unless it's inside Discord: that counts first).
    this.firstDay((d) => {
      if (d.src !== 'w') return false;
      d.src = 'l';
    });
  }

  /** Where this visit came from (net/arrival.ts): kept if it's a new player's first visit (later visits that day leave it). */
  arrived(site: string): void {
    try {
      const store = globalThis.localStorage ?? null;
      const today = this.source().today;
      if (!store || !SITE.test(site) || !dayInfo(today, this.playedBefore, store).isNew || readSite(store)?.date === today) return;
      store.setItem(SITE_KEY, JSON.stringify({ date: today, site } satisfies SiteNote));
    } catch {
      // No storage: where they came from goes unsaid.
    }
  }

  /** The tally so far (for tests and the console). */
  peek(): { counts: Partial<Record<StatKey, number>>; racingMs: number; watchingMs: number; saved: boolean } {
    return { counts: { ...this.counts }, racingMs: this.racingMs, watchingMs: this.watchingMs, saved: this.saved };
  }

  /** Send what's been tallied. Nothing new (or not a player yet), nothing sent. */
  send(): void {
    if (!this.started || !this.engaged) return;
    const racingMs = Math.min(TIME_MAX, Math.round(this.racingMs));
    const watchingMs = Math.min(TIME_MAX, Math.round(this.watchingMs));
    const hasCounts = Object.keys(this.counts).length > 0;
    if (!this.visit && !hasCounts && racingMs < 1000 && watchingMs < 1000 && !this.saved && this.reached <= this.reachedSent) return;
    let body: StatsBody;
    try {
      const s = this.source();
      const day = dayInfo(s.today, this.playedBefore, globalThis.localStorage ?? null);
      body = { playerId: s.playerId, device: s.device, input: s.input, visit: this.visit, ...day, racingMs, watchingMs, saved: this.saved, counts: this.counts, utcOffset: -new Date().getTimezoneOffset() };
      if (s.named !== undefined) body.named = s.named;
      if (this.reopen) body.reopened = true;
      const store = globalThis.localStorage ?? null;
      body.viaLink = this.viaLink;
      const recruit = store?.getItem(RECRUIT_KEY) ?? '';
      body.recruit = /^\d{4}-\d{2}-\d{2}$/.test(recruit) ? recruit : '';
      body.dayTwo = dayTwo(s.today, store);
      if (this.linkOpen) body.link = this.linkOpen;
      if (s.news) body.news = true;
      if (this.reached >= 0) body.reached = this.reached;
      if (this.tookLesson !== undefined) body.tookLesson = this.tookLesson;
      // A first day: how it's going; or, back on a later day for the first time, how it went (then never again).
      const note = readFirstVisit(store);
      const fv = note ? firstVisitOf(note) : null;
      if (note && note.date === s.today && day.isNew) {
        if (fv) body.firstVisit = fv;
      } else if (note && note.date < s.today) {
        const next: FirstVisitNote = { ...note, back: true };
        if (fv && !note.back) body.cameBack = { ...fv, first: note.date, device: note.device };
        // Cohorts: once a day, back on day N, in the groups its first day put it in; "stayed" on its third day within two weeks.
        const day = daysBetween(note.date, s.today);
        if (note.v === 2 && fv && note.ping !== s.today && day >= 1 && day <= COHORT_DAYS) {
          const days = (note.days ?? 1) + 1;
          body.cohort = {
            first: note.date,
            day,
            device: note.device,
            seg: cohortSeg({ far: note.far, src: note.src ?? 'w', lesson: !!note.lesson, board: !!note.saved }),
            ...(days === STAY_DAYS && day <= STAY_WITHIN ? { stayed: true } : {}),
          };
          next.days = days;
          next.ping = s.today;
        }
        if (!note.back || next.ping !== note.ping) store?.setItem(FIRST_VISIT_KEY, JSON.stringify(next));
      }
      // Where they came from: all their first day, and once more on a later day (then never again).
      const site = readSite(store);
      if (site && site.date === s.today && day.isNew) body.site = site.site;
      else if (site && site.date < s.today && !site.back) {
        body.siteBack = { first: site.date, site: site.site };
        store?.setItem(SITE_KEY, JSON.stringify({ ...site, back: true } satisfies SiteNote));
      }
      if (s.discord !== undefined) body.discord = s.discord;
    } catch {
      return;
    }
    this.reachedSent = this.reached;
    this.visit = false;
    this.reopen = false;
    this.linkOpen = null;
    this.counts = {};
    this.racingMs -= racingMs;
    this.watchingMs -= watchingMs;
    this.saved = false;
    this.post(body);
  }

  private post(body: StatsBody, note?: string): void {
    // A request that outlives the page (keepalive) rather than a beacon: ad blockers stop beacons as
    // "hyperlink auditing". Plain text keeps it a simple request; the server reads it as JSON either way.
    try {
      void fetch(`${this.base}events`, { method: 'POST', headers: { 'content-type': 'text/plain' }, body: JSON.stringify(body), keepalive: true }).then(
        (r) => {
          noteTally(note && r.ok ? note : String(r.status));
          // A battle's news rides back on the answer (204 without any).
          if (r.status === 200)
            void r.json().then(
              (o: { battles?: Battle[] }) => {
                if (Array.isArray(o?.battles) && o.battles.length) this.onNews(o.battles);
              },
              () => undefined,
            );
        },
        () => noteTally('failed'),
      );
    } catch {
      // Analytics never gets in the game's way.
      noteTally('failed');
    }
  }
}
